import { Alert, Box, Button, Typography } from "@mui/material";
import { useAsync2, useAsyncAction, useAuthenticated } from "nate-react-api-helpers";
import { Link } from "react-router-dom";
import { api } from "../../api/API";
import { useCurrentUser } from "../../misc/CurrentUser";
import { useUrlState } from "../../misc/urlParams";
import { AccountCard } from "./AccountCard";

// InvitePage is where an invite email links to. A logged-in user with the invited
// address joins from here; anyone else logs in or creates an account first.
export function InvitePage() {
    const url = useUrlState();
    const token = url.text("token");
    const auth = useAuthenticated();
    const me = useCurrentUser();

    const invite = useAsync2(input => api.teams.getInvite(input), { token: token }, [token]);

    const accept = useAsyncAction(async (input: { token: string }) => {
        const team = await api.teams.acceptInvite(input);

        // full page load so the new team is in every list
        window.location.href = "/w/" + team + "/tasks";
    }, []);

    const here = "/invite?token=" + encodeURIComponent(token);

    if (!invite.result) {
        return <AccountCard title="Join a team">{invite.LoadingOrErrorElement}</AccountCard>;
    }

    const value = invite.result;
    const rightAccount = !!me && me.email.trim().toLowerCase() === value.email;

    return (
        <AccountCard title={"Join " + value.teamName}>
            <Typography variant="body2" color="text.secondary" sx={{ pb: 2 }}>
                This invite is for {value.email}.
            </Typography>

            {auth.authenticated && me && rightAccount && (
                <>
                    <Button variant="contained" fullWidth disabled={accept.loading}
                            onClick={() => accept.callback({ token: token })}>
                        Join
                    </Button>
                    {accept.LoadingElement}
                </>
            )}

            {auth.authenticated && me && !rightAccount && (
                <Alert severity="warning">
                    You're logged in as {me.email}. Log out, then open this link again to join as {value.email}.
                    <Box sx={{ pt: 1 }}>
                        <Button size="small" color="inherit" variant="outlined" href="/api/auth/logout">Log out</Button>
                    </Box>
                </Alert>
            )}

            {!auth.authenticated && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    {value.hasAccount && (
                        <Button variant="contained" component={Link}
                                to={"/login?r=" + encodeURIComponent(here)}>
                            Log in to join
                        </Button>
                    )}
                    <Button variant={value.hasAccount ? "text" : "contained"} component={Link}
                            to={"/register?invite=" + encodeURIComponent(token)}>
                        Create account
                    </Button>
                </Box>
            )}
        </AccountCard>
    );
}
